import {
  CampaignSubscriber,
  ContactJourneyEntry,
  NBACampaignBlock,
  NBAComplianceBlock,
  SubscriberLifecycleStatus,
} from '@/shared/types/campaigns';

export type SubscriberStopSource =
  'user_stop' | 'nba_stop' | 'lifecycle_terminal' | 'lifecycle_paused';

export interface SubscriberStopInfo {
  source: SubscriberStopSource;
  reason?: string;
  campaignBlocks: Array<NBACampaignBlock | (string & {})>;
  complianceBlocks: Array<NBAComplianceBlock | (string & {})>;
  /** The subscriber's `lifecycle_status` at the time, if known. */
  lifecycleStatus?: SubscriberLifecycleStatus | (string & {});
  /** Unix ms timestamp of the stop event when the journey records it. */
  stoppedAt?: number;
}

const hasSkipFallback = (subscriber: CampaignSubscriber): boolean =>
  !!(subscriber.metadata as Record<string, unknown> | undefined)?.skip_fallback;

// Only the most recent decision counts — a resume or a newer NBA output supersedes an old stop.
const findLastDecision = (journey: ContactJourneyEntry[] | undefined) => {
  let last: ContactJourneyEntry | undefined;
  for (const entry of journey ?? []) {
    if (entry.nba_output || entry.status_change) {
      last = entry;
    }
  }
  return last;
};

/**
 * Lifecycle states that mean outreach is not currently happening. `ok` is the
 * only "actively engaging" state — everything else (terminal stops, paused,
 * legal hold, disputes, etc.) means we should surface a stop signal.
 */
const NON_ACTIVE_LIFECYCLE_STATES = new Set<string>([
  'paused',
  'in_dispute',
  'ceased',
  'wrong_party',
  'legal_hold',
  'success',
  'stopped',
  'failed',
]);

const isPausedLifecycle = (status: string | undefined): boolean => status === 'paused';

export const getSubscriberStopInfo = (
  subscriber: CampaignSubscriber,
  journey?: ContactJourneyEntry[]
): SubscriberStopInfo | null => {
  const lifecycleStatus = subscriber.lifecycle_status;

  const lastDecision = findLastDecision(journey);
  const stopChange =
    lastDecision?.status_change?.action === 'stopped' ? lastDecision.status_change : undefined;
  if (stopChange?.reason === 'manual') {
    return {
      source: 'user_stop',
      campaignBlocks: [],
      complianceBlocks: [],
      lifecycleStatus,
      stoppedAt: stopChange.timestamp,
    };
  }

  const stopOutput =
    lastDecision?.nba_output?.action?.label === 'stop' ? lastDecision.nba_output : undefined;
  if (stopOutput) {
    return {
      source: 'nba_stop',
      reason: stopOutput.action?.reason,
      campaignBlocks: stopOutput.action?.campaign_blocks ?? [],
      complianceBlocks: stopOutput.action?.compliance_blocks ?? [],
      lifecycleStatus,
      stoppedAt: stopOutput.timestamp,
    };
  }

  // `skip_fallback` is not proof of a user stop, so use it only when there is no journey.
  if (!journey?.length && hasSkipFallback(subscriber)) {
    return { source: 'user_stop', campaignBlocks: [], complianceBlocks: [], lifecycleStatus };
  }

  if (lifecycleStatus && NON_ACTIVE_LIFECYCLE_STATES.has(lifecycleStatus)) {
    return {
      source: isPausedLifecycle(lifecycleStatus) ? 'lifecycle_paused' : 'lifecycle_terminal',
      campaignBlocks: [],
      complianceBlocks: [],
      lifecycleStatus,
      stoppedAt: stopChange?.timestamp,
    };
  }

  return null;
};

const CAMPAIGN_BLOCK_BASE = 'campaigns.subscriber_journey.stop_block.campaign.';
const COMPLIANCE_BLOCK_BASE = 'campaigns.subscriber_journey.stop_block.compliance.';
const LIFECYCLE_BASE = 'campaigns.subscriber_journey.stop_block.lifecycle.';

const CAMPAIGN_BLOCKS: ReadonlySet<string> = new Set<NBACampaignBlock>([
  'budget_exhausted',
  'paused_contact',
  'llm_decided_stop',
  'llm_stop',
  'llm_failure',
  'channel_not_allowed',
  'no_available_channels',
  'no_integration_mapping',
  'payment_confirmed',
  'goal_achieved',
  'no_channel_consent',
]);

/**
 * Canonical compliance codes that carry their own copy. Regional variants
 * (`frequency_capping_gr`, `quiet_hours_br_rj`, etc.) are aliased to one of
 * these via COMPLIANCE_BASE_ALIAS below.
 */
const COMPLIANCE_BLOCKS: ReadonlySet<string> = new Set<NBAComplianceBlock>([
  'opt_out',
  'channel_opt_out',
  'cease_desist',
  'refusal_to_pay',
  'dispute',
  'identity_theft',
  'coerced_debt',
  'bankruptcy',
  'attorney_representation',
  'deceased',
  'wrong_party',
  'scra_protection',
  'overdue_grace_period',
  'public_holidays',
  'consent',
  'inconvenient_contact',
  'quiet_hours',
  'frequency_capping',
]);

const LIFECYCLE_STATES: ReadonlySet<string> = new Set<SubscriberLifecycleStatus>([
  'ok',
  'pending',
  'paused',
  'in_dispute',
  'ceased',
  'wrong_party',
  'legal_hold',
  'success',
  'stopped',
  'failed',
]);

const UNKNOWN_KEY = 'campaigns.subscriber_journey.stop_block.unknown';

/**
 * Compliance variants that share copy. Dialog-service models each jurisdictional
 * rule as a distinct code (e.g. `frequency_capping_gr`, `quiet_hours_br_rj`), but
 * the user-facing message is identical. We map every variant back to the base
 * code so there's one translation per family instead of N per locale.
 */
const COMPLIANCE_BASE_ALIAS: Record<string, string> = {
  frequency_capping_ca: 'frequency_capping',
  frequency_capping_ma: 'frequency_capping',
  frequency_capping_gr: 'frequency_capping',
  frequency_capping_br: 'frequency_capping',
  quiet_hours_br: 'quiet_hours',
  quiet_hours_br_rj: 'quiet_hours',
  quiet_hours_br_es: 'quiet_hours',
  quiet_hours_br_pr: 'quiet_hours',
  quiet_hours_br_am: 'quiet_hours',
};

export const getCampaignBlockLabelKey = (block: string): string =>
  CAMPAIGN_BLOCKS.has(block) ? `${CAMPAIGN_BLOCK_BASE}${block}` : UNKNOWN_KEY;

export const getComplianceBlockLabelKey = (block: string): string => {
  const canonical = COMPLIANCE_BASE_ALIAS[block] ?? block;
  return COMPLIANCE_BLOCKS.has(canonical) ? `${COMPLIANCE_BLOCK_BASE}${canonical}` : UNKNOWN_KEY;
};

export const getLifecycleLabelKey = (status: string): string =>
  LIFECYCLE_STATES.has(status) ? `${LIFECYCLE_BASE}${status}` : UNKNOWN_KEY;

// Lifecycle states that aren't "stopped" now. 'ok' included so a resumed subscriber
// (lifecycle flipped to 'ok' but stale journey stop / skip_fallback linger) reads as active.
// 'pending' stays out: non-agentic stops leave lifecycle at 'pending' and only set skip_fallback.
const NON_STOPPED_LIFECYCLE_STATES: ReadonlySet<string> = new Set(['success', 'paused', 'ok']);

export const shouldShowStoppedBadge = (info: SubscriberStopInfo | null): boolean => {
  if (!info) {
    return false;
  }
  if (!info.lifecycleStatus) {
    return true;
  }
  return !NON_STOPPED_LIFECYCLE_STATES.has(info.lifecycleStatus);
};

// Permanent stops that must not be resumed: goal achieved (payment_confirmed is its debt equivalent).
const PERMANENT_STOP_BLOCKS: ReadonlySet<string> = new Set(['goal_achieved', 'payment_confirmed']);

/** Whether a stopped subscriber can be manually resumed — every stop except permanent ones. */
export const isSubscriberResumable = (info: SubscriberStopInfo | null): boolean => {
  // shouldShowStoppedBadge already excludes success/paused.
  if (!info || !shouldShowStoppedBadge(info)) {
    return false;
  }
  return !info.campaignBlocks.some((block) => PERMANENT_STOP_BLOCKS.has(block));
};

/**
 * The headline title for a stop. Priority: compliance > campaign > lifecycle > user > unknown.
 * Compliance reasons are the most specific and most important for the user to see.
 */
export const getStopTitleKey = (info: SubscriberStopInfo): string => {
  if (info.source === 'user_stop') {
    return 'campaigns.subscriber_journey.stop_block.user_stop';
  }
  const firstCompliance = info.complianceBlocks[0];
  if (firstCompliance) {
    return getComplianceBlockLabelKey(firstCompliance);
  }
  const firstCampaign = info.campaignBlocks[0];
  if (firstCampaign) {
    return getCampaignBlockLabelKey(firstCampaign);
  }
  if (info.lifecycleStatus) {
    return getLifecycleLabelKey(info.lifecycleStatus);
  }
  return UNKNOWN_KEY;
};
